import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import PublicPaymentSummaryTable from '../features/summary/PublicPaymentSummaryTable';
import PageHeader from '../components/PageHeader';
import ThemeToggle from '../components/ThemeToggle';
import { Theme } from '../theme';
import { SchoolYear } from '../types';
import { formatMoney } from '../utils/format';

interface BalanceVerification {
    expectedBalance: number;
    calculatedBalance: number;
    difference: number;
}

const PublicSummaryPage: React.FC<{ theme: Theme; onToggleTheme: () => void }> = ({ theme, onToggleTheme }) => {
    const { secret } = useParams<{ secret: string }>();
    const [selectedSchoolYear, setSelectedSchoolYear] = useState<SchoolYear | null>(null);
    const [balanceData, setBalanceData] = useState<BalanceVerification | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (secret) {
            axios.get<SchoolYear[]>(`/api/public/${secret}/school-years`)
                .then(response => {
                    const years = response.data;
                    const currentYear = years.find((y: SchoolYear) => y.current);
                    if (currentYear) {
                        setSelectedSchoolYear(currentYear);
                    } else if (years.length > 0) {
                        setSelectedSchoolYear(years[0]);
                    }
                })
                .catch(err => {
                    if (err.response && err.response.status === 403) {
                        setError("Odmowa dostępu. Nieprawidłowy link.");
                    } else {
                        setError("Wystąpił błąd podczas ładowania danych.");
                    }
                });

            axios.get<BalanceVerification>(`/api/public/${secret}/balance-verification`)
                .then(response => {
                    setBalanceData(response.data);
                })
                .catch(err => {
                    console.error("Failed to fetch balance verification:", err);
                });
        }
    }, [secret]);

    if (error) {
        return <div className="public-page"><div className="alert alert-danger" role="alert">{error}</div></div>;
    }

    if (!selectedSchoolYear || !secret) {
        return (
            <div className="public-page">
                <div className="loading-state"><span className="spinner-border spinner-border-sm" role="status" />Ładowanie…</div>
            </div>
        );
    }

    return (
        <div className="public-page">
            <PageHeader
                title="Podsumowanie"
                subtitle={`Rok szkolny ${selectedSchoolYear.name}`}
                actions={<ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            />

            {balanceData && (
                <section className="stat-grid" aria-label="Saldo konta klasowego">
                    <div className="stat-card surface">
                        <div className="stat-label">Saldo konta klasowego</div>
                        <div className="stat-value">{formatMoney(balanceData.calculatedBalance, { fixed: true })}</div>
                    </div>
                </section>
            )}

            <PublicPaymentSummaryTable
                secret={secret}
                startDate={selectedSchoolYear.startDate}
                endDate={selectedSchoolYear.endDate}
                schoolYear={selectedSchoolYear.name}
            />
        </div>
    );
};

export default PublicSummaryPage;
